import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getAuth } from 'firebase/auth';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { API_URL } from '../../constants/string-constants';
import ReportDetailsModal from './ReportDetailsModal';
import ModBanModal from './ModBanModal';
dayjs.extend(relativeTime);
import { toast } from 'react-toastify';

const FullScreenOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: var(--clr-bg);
  z-index: 2000;
  display: flex;
  flex-direction: column;
`;

const Header = styled.div`
  background-color: var(--clr-prim);
  padding: 1rem 2rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const Title = styled.h1`
  color: var(--clr-sec);
  margin: 0;
  font-size: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const RoomBadge = styled.span`
  background-color: var(--clr-purple);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.8rem;
  font-weight: 500;
  text-transform: uppercase;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: var(--clr-sec);
  font-size: 1.5rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 6px;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
  }
`;

const Content = styled.div`
  flex: 1;
  padding: 2rem;
  overflow-y: auto;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
`;

const StatsSection = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const StatCard = styled.div`
  background-color: rgba(255, 255, 255, 0.05);
  padding: 0.4rem 0.75rem;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 0.4rem;
`;

const StatNumber = styled.div`
  font-size: 1rem;
  font-weight: 700;
  color: var(--clr-purple);
`;

const StatLabel = styled.div`
  color: var(--clr-sec);
  font-size: 0.8rem;
  opacity: 0.7;
`;

const ReportsList = styled.div`
  background-color: var(--clr-prim);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  overflow: hidden;
`;

const ReportsHeader = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const SectionTitle = styled.h2`
  color: var(--clr-sec);
  margin: 0;
  font-size: 1.2rem;
`;

const RefreshButton = styled.button`
  background-color: var(--clr-purple);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s;
  
  &:hover:not(:disabled) {
    background-color: #b060d4;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ReportItem = styled.div`
  padding: 1.5rem 2rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: rgba(255, 255, 255, 0.02);
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const UserInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  flex: 1;
`;

const UserDisplay = styled.div`
  color: var(--clr-sec);
  font-size: 0.95rem;
  font-weight: 500;
`;

const UserId = styled.span`
  color: var(--clr-sec);
  font-family: monospace;
  font-size: 0.8rem;
  opacity: 0.7;
  margin-left: 0.5rem;
`;

const ReportCount = styled.div`
  color: var(--clr-sec);
  opacity: 0.7;
  font-size: 0.8rem;
`;

const RoomBreakdown = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.25rem;
`;

const RoomBadgeSmall = styled.span`
  background-color: var(--clr-purple);
  color: white;
  padding: 0.15rem 0.4rem;
  border-radius: 8px;
  font-size: 0.7rem;
  font-weight: 500;
  opacity: 0.8;
`;

const TRUST_BADGE_COLORS = ['#ff4444', '#ff8c00', '#4a90d9', '#3cb371'];
const TRUST_BADGE_NAMES = ['Restricted', 'New', 'Member', 'Regular'];

const TrustBadgeSmall = styled.span`
  background-color: ${(props) => TRUST_BADGE_COLORS[props.$level] || '#ff8c00'};
  color: white;
  padding: 0.15rem 0.4rem;
  border-radius: 8px;
  font-size: 0.7rem;
  font-weight: 500;
  opacity: 0.8;
`;

const LastReportTime = styled.div`
  color: var(--clr-sec);
  opacity: 0.6;
  font-size: 0.8rem;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  align-items: center;
`;

const ViewButton = styled.button`
  background-color: var(--clr-purple);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #b060d4;
  }
`;

const FriendButton = styled.button`
  background-color: var(--clr-purple);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    opacity: 0.85;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const BanButton = styled.button`
  background-color: #ff4444;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.8rem;
  transition: background-color 0.2s;

  &:hover {
    background-color: #dd3333;
  }
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--clr-sec);
  opacity: 0.7;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 3rem;
  color: #ff4444;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--clr-sec);
  opacity: 0.7;
`;

const UserSearchSection = styled.div`
  background-color: var(--clr-prim);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 2rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
`;

const SlowModeButtons = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 0.75rem;
`;

const SlowModeButton = styled.button`
  background-color: ${(props) => (props.$active ? 'var(--clr-purple)' : 'rgba(255, 255, 255, 0.08)')};
  color: white;
  border: 1px solid ${(props) => (props.$active ? 'var(--clr-purple)' : 'rgba(255, 255, 255, 0.15)')};
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  font-weight: ${(props) => (props.$active ? '700' : '400')};
  transition: background-color 0.2s, border-color 0.2s;

  &:hover:not(:disabled) {
    background-color: ${(props) => (props.$active ? '#b060d4' : 'rgba(255, 255, 255, 0.16)')};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SlowModeCustomRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-top: 0.6rem;
`;

const SlowModeCustomInput = styled.input`
  width: 70px;
  background-color: rgba(255, 255, 255, 0.08);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  padding: 0.5rem 0.6rem;
  font-size: 0.85rem;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SlowModeHint = styled.p`
  color: var(--clr-sec);
  font-size: 0.85rem;
  margin: 0.5rem 0 0 0;
`;

const SlowModeRoomRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.85rem;
`;

const SlowModeRoomLabel = styled.span`
  color: var(--clr-sec);
  font-size: 0.85rem;
`;

const SlowModeRoomSelect = styled.select`
  background-color: rgba(255, 255, 255, 0.08);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 6px;
  padding: 0.4rem 0.6rem;
  font-size: 0.85rem;
  cursor: pointer;

  & option {
    color: black;
  }
`;

const UserSearchResults = styled.div`
  margin-top: 1rem;
  max-height: 400px;
  overflow-y: auto;
`;

const UserSearchItem = styled.div`
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  border-radius: 8px;
  background-color: var(--clr-bg);
  margin-bottom: 0.5rem;
  gap: 0.75rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const OnlineIndicator = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => props.$isOnline ? '#4caf50' : '#666'};
  flex-shrink: 0;
`;

const UserSearchInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  flex: 1;
  min-width: 0;
`;

const UserSearchName = styled.div`
  color: var(--clr-sec);
  font-size: 0.95rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const UserSearchMeta = styled.div`
  color: var(--clr-sec);
  opacity: 0.6;
  font-size: 0.75rem;
`;

const BansSection = styled.div`
  background-color: var(--clr-prim);
  border-radius: 12px;
  padding: 1.5rem;
  margin-top: 2rem;
  margin-bottom: 2rem;
`;

const BansSectionTitle = styled.h2`
  color: var(--clr-sec);
  margin: 0 0 1rem 0;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const BansList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const BanItem = styled.div`
  background-color: var(--clr-bg);
  border-radius: 8px;
  padding: 1rem;
  border-left: 4px solid #e74c3c;
`;

const BanDetails = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
`;

const BanInfo = styled.div`
  flex: 1;
`;

const InternalNote = styled.div`
  color: var(--clr-sec);
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
`;

const InternalNoteLabel = styled.span`
  color: var(--clr-sec);
  opacity: 0.7;
  font-weight: 600;
`;

const ExternalReason = styled.div`
  color: var(--clr-sec);
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
`;

const ExternalReasonLabel = styled.span`
  color: var(--clr-sec);
  opacity: 0.7;
  font-weight: 600;
`;

const BannedMessage = styled.div`
  color: var(--clr-sec);
  font-size: 0.9rem;
  margin-bottom: 0.25rem;
  font-style: italic;
  background-color: rgba(255, 255, 255, 0.05);
  padding: 0.5rem;
  border-radius: 4px;
  margin-top: 0.5rem;
`;

const BannedMessageLabel = styled.span`
  color: var(--clr-sec);
  opacity: 0.7;
  font-weight: 600;
  font-style: normal;
`;

const BannedUser = styled.div`
  color: var(--clr-sec);
  font-size: 0.95rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const BannedUserLabel = styled.span`
  color: var(--clr-sec);
  opacity: 0.7;
  font-weight: 600;
`;

const BanId = styled.div`
  color: var(--clr-sec);
  font-size: 0.8rem;
  font-family: monospace;
  opacity: 0.6;
  margin-bottom: 0.5rem;
`;

const BanIdLabel = styled.span`
  color: var(--clr-sec);
  opacity: 0.7;
  font-weight: 600;
`;

const BanTiming = styled.div`
  text-align: right;
  font-size: 0.8rem;
`;

const BanTime = styled.div`
  color: var(--clr-sec);
  opacity: 0.7;
`;

const BanExpiry = styled.div`
  color: #e74c3c;
  font-weight: 500;
  margin-top: 0.25rem;
`;

const EmptyBans = styled.div`
  text-align: center;
  color: var(--clr-sec);
  opacity: 0.6;
  padding: 4rem 2rem;
  font-style: italic;
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const UnbanButton = styled.button`
  background-color: var(--clr-green);
  color: white;
  border: none;
  padding: 0.4rem 0.8rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.75rem;
  transition: background-color 0.2s;
  margin-top: 0.5rem;

  &:hover {
    background-color: #28a745;
  }

  &:disabled {
    background-color: #999;
    cursor: not-allowed;
  }
`;

const EditBanButton = styled.button`
  background-color: var(--clr-purple);
  color: white;
  border: none;
  padding: 0.4rem 0.8rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.75rem;
  transition: background-color 0.2s;
  margin-top: 0.5rem;
  margin-left: 0.5rem;

  &:hover:not(:disabled) {
    background-color: #b060d4;
  }

  &:disabled {
    background-color: #999;
    cursor: not-allowed;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 3000;
`;

const ModalContent = styled.div`
  background-color: var(--clr-prim);
  border-radius: 12px;
  padding: 2rem;
  max-width: 500px;
  width: 90%;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
`;

const ModalTitle = styled.h3`
  color: var(--clr-sec);
  margin: 0 0 1.5rem 0;
  font-size: 1.3rem;
`;

const ModalLabel = styled.label`
  display: block;
  color: var(--clr-sec);
  font-size: 0.9rem;
  margin-bottom: 0.5rem;
  font-weight: 500;
`;

const ModalInput = styled.input`
  width: 100%;
  background-color: var(--clr-bg);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 0.75rem;
  color: var(--clr-sec);
  font-size: 0.95rem;
  margin-bottom: 1.5rem;

  &:focus {
    outline: none;
    border-color: var(--clr-purple);
  }
`;

const ModalButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
`;

const ModalButton = styled.button`
  padding: 0.6rem 1.2rem;
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: 500;
  transition: background-color 0.2s;

  &.primary {
    background-color: var(--clr-purple);
    color: white;

    &:hover:not(:disabled) {
      background-color: #b060d4;
    }

    &:disabled {
      background-color: #999;
      cursor: not-allowed;
    }
  }

  &.secondary {
    background-color: rgba(255, 255, 255, 0.1);
    color: var(--clr-sec);

    &:hover {
      background-color: rgba(255, 255, 255, 0.15);
    }
  }
`;

const SearchForm = styled.form`
  display: flex;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const SearchInput = styled.input`
  flex: 1;
  background-color: var(--clr-bg);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  padding: 0.5rem 1rem;
  color: var(--clr-sec);
  font-size: 0.9rem;

  &:focus {
    outline: none;
    border-color: var(--clr-purple);
  }

  &::placeholder {
    color: var(--clr-sec);
    opacity: 0.5;
  }
`;

const ClearButton = styled.button`
  background-color: rgba(255, 255, 255, 0.1);
  color: var(--clr-sec);
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: background-color 0.2s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.15);
  }
`;

const PaginationControls = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 1rem;
  margin-top: 1rem;
  padding: 1rem;
`;

const PaginationButton = styled.button`
  background-color: var(--clr-purple);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  cursor: pointer;
  font-size: 0.85rem;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background-color: #b060d4;
  }

  &:disabled {
    background-color: rgba(255, 255, 255, 0.1);
    color: var(--clr-sec);
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const PaginationInfo = styled.div`
  color: var(--clr-sec);
  font-size: 0.9rem;
`;

const SkeletonBanItem = styled.div`
  background-color: var(--clr-bg);
  border-radius: 8px;
  padding: 1rem;
  border-left: 4px solid rgba(231, 76, 60, 0.3);
  animation: pulse 1.5s ease-in-out infinite;

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

const SkeletonLine = styled.div`
  background-color: rgba(255, 255, 255, 0.1);
  height: ${props => props.height || '1rem'};
  width: ${props => props.width || '100%'};
  border-radius: 4px;
  margin-bottom: ${props => props.marginBottom || '0.5rem'};
`;

const ModReportsDashboard = ({ isOpen, onClose, room = 'general', currentRoomId = 'en-chat', isGlobalMod = false, onReportsChanged }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [reportDetailsOpen, setReportDetailsOpen] = useState(false);
  const [banModalOpen, setBanModalOpen] = useState(false);
  const [userToBan, setUserToBan] = useState(null);
  const [recentBans, setRecentBans] = useState([]);
  const [bansLoading, setBansLoading] = useState(false);
  const [unbanningId, setUnbanningId] = useState(null);
  const [bansPage, setBansPage] = useState(1);
  const [bansPagination, setBansPagination] = useState(null);
  const [bansSearch, setBansSearch] = useState('');
  const [editBanModalOpen, setEditBanModalOpen] = useState(false);
  const [banToEdit, setBanToEdit] = useState(null);
  const [editUnbanDate, setEditUnbanDate] = useState('');
  const [editInternalNote, setEditInternalNote] = useState('');
  const [editExternalReason, setEditExternalReason] = useState('');
  const [editingBan, setEditingBan] = useState(false);
  const [userSearch, setUserSearch] = useState('');
  const [userSearchResults, setUserSearchResults] = useState([]);
  const [userSearchLoading, setUserSearchLoading] = useState(false);
  const [friendRequestLoading, setFriendRequestLoading] = useState({});
  const [friendRequestSent, setFriendRequestSent] = useState({});
  const [underageBans, setUnderageBans] = useState([]);
  const [underageBansLoading, setUnderageBansLoading] = useState(false);
  const [underageBansPage, setUnderageBansPage] = useState(1);
  const [underageBansPagination, setUnderageBansPagination] = useState(null);
  const [underageBansSearch, setUnderageBansSearch] = useState('');
  const [removingUnderageBanId, setRemovingUnderageBanId] = useState(null);
  const [vpnBans, setVpnBans] = useState([]);
  const [vpnBansLoading, setVpnBansLoading] = useState(false);
  const [vpnBansPage, setVpnBansPage] = useState(1);
  const [vpnBansPagination, setVpnBansPagination] = useState(null);
  const [vpnBansSearch, setVpnBansSearch] = useState('');
  const [unbanningVpnId, setUnbanningVpnId] = useState(null);
  const [slowModeSeconds, setSlowModeSeconds] = useState(0);
  const [slowModeSaving, setSlowModeSaving] = useState(false);
  const [slowModeCustom, setSlowModeCustom] = useState('');
  // Slow mode is per-room. In the global ("All Default Rooms") view there is no
  // single room in context, so the mod picks a target room here. In a
  // room-scoped view it's fixed to that room. Defaults to the room the mod is
  // currently in.
  const [slowModeTargetRoom, setSlowModeTargetRoom] = useState(
    room !== 'global' ? room : currentRoomId
  );
  const [slowModeRooms, setSlowModeRooms] = useState([]);

  const SLOW_MODE_PRESETS = [0, 3, 5, 10, 30];
  const SLOW_MODE_MIN = 1;
  const SLOW_MODE_MAX = 30;

  const handleSetCustomSlowMode = () => {
    const n = Math.floor(Number(slowModeCustom));
    if (!Number.isFinite(n) || n < SLOW_MODE_MIN || n > SLOW_MODE_MAX) {
      setError(`Slow mode must be a whole number between ${SLOW_MODE_MIN} and ${SLOW_MODE_MAX} seconds.`);
      return;
    }
    setSlowModeCustom('');
    handleSetSlowMode(n);
  };

  // The room the slow-mode controls actually act on.
  const effectiveSlowModeRoom = room !== 'global' ? room : slowModeTargetRoom;

  const fetchSlowModeRooms = async () => {
    try {
      const token = await getAuth().currentUser.getIdToken();
      const response = await fetch(`${API_URL}rooms`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        const rooms = (data.rooms || []).filter((r) => r && r.id);
        setSlowModeRooms(rooms);
        // Make sure the selected target is a valid room.
        if (rooms.length && !rooms.some((r) => r.id === slowModeTargetRoom)) {
          setSlowModeTargetRoom(
            rooms.some((r) => r.id === currentRoomId) ? currentRoomId : rooms[0].id
          );
        }
      }
    } catch (err) {
      // Non-fatal: dropdown just won't populate; controls still work for the default room.
    }
  };

  const fetchSlowMode = async (targetRoom = effectiveSlowModeRoom) => {
    if (!targetRoom) return;
    try {
      const token = await getAuth().currentUser.getIdToken();
      const response = await fetch(`${API_URL}mod/room-slow-mode?room=${targetRoom}`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (response.ok) {
        const data = await response.json();
        setSlowModeSeconds(data.seconds || 0);
      }
    } catch (err) {
      // Non-fatal: leave the control at its last known value.
    }
  };

  const handleSetSlowMode = async (seconds) => {
    if (!effectiveSlowModeRoom || slowModeSaving) return;
    setSlowModeSaving(true);
    try {
      const token = await getAuth().currentUser.getIdToken();
      const response = await fetch(`${API_URL}mod/room-slow-mode`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ room: effectiveSlowModeRoom, seconds }),
      });
      if (response.ok) {
        const data = await response.json();
        setSlowModeSeconds(data.seconds || 0);
      } else {
        const errorData = await response.json().catch(() => ({}));
        setError(errorData.error || 'Failed to update slow mode');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setSlowModeSaving(false);
    }
  };

  const fetchReports = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = await getAuth().currentUser.getIdToken();
      
      // Use global endpoint for global mods, room-specific endpoint otherwise
      const endpoint = room === 'global' ? 
        `${API_URL}report/mod/global` : 
        `${API_URL}report/mod/${room}`;
      
      const response = await fetch(endpoint, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setReports(data.reports || []);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to fetch reports');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchRecentBans = async (page = 1, search = '') => {
    setBansLoading(true);

    try {
      const token = await getAuth().currentUser.getIdToken();
      const roomParam = room !== 'global' ? `room=${room}` : '';
      const pageParam = `page=${page}`;
      const limitParam = `limit=10`;
      const searchParam = search ? `search=${encodeURIComponent(search)}` : '';

      const params = [roomParam, pageParam, limitParam, searchParam].filter(p => p).join('&');
      const url = `${API_URL}mod/recent-ip-bans${params ? `?${params}` : ''}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setRecentBans(data.bans || []);
        setBansPagination(data.pagination || null);
      } else {
        console.error('Failed to fetch recent bans');
      }
    } catch (err) {
      console.error('Error fetching recent bans:', err);
    } finally {
      setBansLoading(false);
    }
  };

  const fetchUnderageBans = async (page = 1, search = '') => {
    setUnderageBansLoading(true);

    try {
      const token = await getAuth().currentUser.getIdToken();
      const roomParam = room !== 'global' ? `room=${room}` : '';
      const pageParam = `page=${page}`;
      const limitParam = `limit=10`;
      const searchParam = search ? `search=${encodeURIComponent(search)}` : '';

      const params = [roomParam, pageParam, limitParam, searchParam].filter(p => p).join('&');
      const url = `${API_URL}mod/underage-bans${params ? `?${params}` : ''}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setUnderageBans(data.bans || []);
        setUnderageBansPagination(data.pagination || null);
      } else {
        console.error('Failed to fetch underage bans');
      }
    } catch (err) {
      console.error('Error fetching underage bans:', err);
    } finally {
      setUnderageBansLoading(false);
    }
  };

  const handleRemoveUnderageBan = async (banId) => {
    setRemovingUnderageBanId(banId);

    try {
      const token = await getAuth().currentUser.getIdToken();
      const roomParam = room !== 'global' ? `?room=${room}` : '';

      const response = await fetch(`${API_URL}mod/remove-underage-ban/${banId}${roomParam}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });

      if (response.ok) {
        toast.success('Underage restriction removed successfully');
        fetchUnderageBans(underageBansPage, underageBansSearch);
      } else {
        const errorData = await response.json();
        toast.error(`Failed to remove restriction: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error removing underage ban:', err);
      toast.error('Network error while removing restriction');
    } finally {
      setRemovingUnderageBanId(null);
    }
  };

  const fetchVpnBans = async (page = 1, search = '') => {
    setVpnBansLoading(true);

    try {
      const token = await getAuth().currentUser.getIdToken();
      const roomParam = room !== 'global' ? `room=${room}` : '';
      const pageParam = `page=${page}`;
      const limitParam = `limit=10`;
      const searchParam = search ? `search=${encodeURIComponent(search)}` : '';

      const params = [roomParam, pageParam, limitParam, searchParam].filter(p => p).join('&');
      const url = `${API_URL}mod/vpn-auto-bans${params ? `?${params}` : ''}`;

      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setVpnBans(data.bans || []);
        setVpnBansPagination(data.pagination || null);
      } else {
        console.error('Failed to fetch VPN auto-bans');
      }
    } catch (err) {
      console.error('Error fetching VPN auto-bans:', err);
    } finally {
      setVpnBansLoading(false);
    }
  };

  const handleUnbanVpn = async (banId) => {
    setUnbanningVpnId(banId);

    try {
      const token = await getAuth().currentUser.getIdToken();
      const roomParam = room !== 'global' ? `?room=${room}` : '';

      const response = await fetch(`${API_URL}mod/unban-vpn/${banId}${roomParam}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });

      if (response.ok) {
        toast.success('VPN auto-ban removed and cache cleared');
        fetchVpnBans(vpnBansPage, vpnBansSearch);
      } else {
        const errorData = await response.json();
        toast.error(`Failed to unban: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error unbanning VPN auto-ban:', err);
      toast.error('Network error while unbanning user');
    } finally {
      setUnbanningVpnId(null);
    }
  };

  const handleUnban = async (userId) => {
    if (!userId) {
      toast.error('Cannot unban: no user ID available');
      return;
    }
    setUnbanningId(userId);

    try {
      const token = await getAuth().currentUser.getIdToken();
      const roomParam = room !== 'global' ? `?room=${room}` : '';

      const response = await fetch(`${API_URL}mod/unban-user/${userId}${roomParam}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
      });

      if (response.ok) {
        toast.success('User unbanned successfully');
        // Refresh the bans list
        fetchRecentBans(bansPage, bansSearch);
      } else {
        const errorData = await response.json();
        toast.error(`Failed to unban: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error unbanning:', err);
      toast.error('Network error while unbanning user');
    } finally {
      setUnbanningId(null);
    }
  };

  const handleOpenEditBanModal = (ban) => {
    setBanToEdit(ban);
    // Set default to current unban date formatted for datetime-local input
    const currentDate = new Date(ban.unbanDate);
    const formatted = currentDate.toISOString().slice(0, 16);
    setEditUnbanDate(formatted);
    setEditInternalNote(ban.internalNote || '');
    setEditExternalReason(ban.externalReason || '');
    setEditBanModalOpen(true);
  };

  const handleCloseEditBanModal = () => {
    setEditBanModalOpen(false);
    setBanToEdit(null);
    setEditUnbanDate('');
    setEditInternalNote('');
    setEditExternalReason('');
  };

  const handleEditBan = async () => {
    if (!banToEdit) return;

    setEditingBan(true);

    try {
      const token = await getAuth().currentUser.getIdToken();
      const roomParam = room !== 'global' ? `?room=${room}` : '';

      const response = await fetch(`${API_URL}mod/edit-ban/${banToEdit.id}${roomParam}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          newUnbanDate: editUnbanDate ? new Date(editUnbanDate).toISOString() : undefined,
          newInternalNote: editInternalNote,
          newExternalReason: editExternalReason,
        }),
      });

      if (response.ok) {
        toast.success('Ban updated successfully');
        handleCloseEditBanModal();
        // Refresh the bans list
        fetchRecentBans(bansPage, bansSearch);
      } else {
        const errorData = await response.json();
        toast.error(`Failed to update ban: ${errorData.error || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error updating ban:', err);
      toast.error('Network error while updating ban');
    } finally {
      setEditingBan(false);
    }
  };

  useEffect(() => {
    if (isOpen) {
      fetchReports();
      fetchRecentBans(bansPage, bansSearch);
      fetchUnderageBans(underageBansPage, underageBansSearch);
      fetchVpnBans(vpnBansPage, vpnBansSearch);
      // Populate the room dropdown (global view) and load the current slow-mode
      // state for the target room.
      if (room === 'global') fetchSlowModeRooms();
      fetchSlowMode();
    }
  }, [isOpen, room]);

  // When the mod picks a different target room (global view), re-pull that
  // room's current slow-mode state so the presets reflect reality.
  useEffect(() => {
    if (isOpen && room === 'global' && slowModeTargetRoom) {
      fetchSlowMode(slowModeTargetRoom);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slowModeTargetRoom]);

  useEffect(() => {
    if (isOpen) {
      fetchRecentBans(bansPage, bansSearch);
    }
  }, [bansPage, bansSearch]);

  useEffect(() => {
    if (isOpen) {
      fetchUnderageBans(underageBansPage, underageBansSearch);
    }
  }, [underageBansPage, underageBansSearch]);

  // Debounced search effect
  useEffect(() => {
    if (!isOpen) return;

    const debounceTimer = setTimeout(() => {
      setBansPage(1); // Reset to first page on new search
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [bansSearch]);

  // Debounced search effect for underage bans
  useEffect(() => {
    if (!isOpen) return;

    const debounceTimer = setTimeout(() => {
      setUnderageBansPage(1);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [underageBansSearch]);

  useEffect(() => {
    if (isOpen) {
      fetchVpnBans(vpnBansPage, vpnBansSearch);
    }
  }, [vpnBansPage, vpnBansSearch]);

  // Debounced search effect for VPN bans
  useEffect(() => {
    if (!isOpen) return;

    const debounceTimer = setTimeout(() => {
      setVpnBansPage(1);
    }, 500);

    return () => clearTimeout(debounceTimer);
  }, [vpnBansSearch]);

  const searchUsers = async (query) => {
    if (query.length < 2) {
      setUserSearchResults([]);
      return;
    }
    setUserSearchLoading(true);
    try {
      const token = await getAuth().currentUser.getIdToken();
      const response = await fetch(`${API_URL}mod/search-users?q=${encodeURIComponent(query)}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUserSearchResults(data.users || []);
      }
    } catch (err) {
      console.error('Error searching users:', err);
    } finally {
      setUserSearchLoading(false);
    }
  };

  const handleSendFriendRequest = async (userId) => {
    setFriendRequestLoading(prev => ({ ...prev, [userId]: true }));
    try {
      const token = await getAuth().currentUser.getIdToken();
      const response = await fetch(`${API_URL}friends/request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ receiver_id: userId }),
      });
      if (response.ok) {
        setFriendRequestSent(prev => ({ ...prev, [userId]: true }));
        toast.success('Friend request sent!');
      } else {
        const errorData = await response.json();
        toast.error(errorData.error || 'Failed to send friend request');
      }
    } catch (err) {
      console.error('Error sending friend request:', err);
      toast.error('Failed to send friend request');
    } finally {
      setFriendRequestLoading(prev => ({ ...prev, [userId]: false }));
    }
  };

  // Debounced user search effect
  useEffect(() => {
    if (!isOpen) return;

    const timer = setTimeout(() => {
      if (userSearch) {
        searchUsers(userSearch);
      } else {
        setUserSearchResults([]);
      }
    }, 400);

    return () => clearTimeout(timer);
  }, [userSearch, isOpen]);

  const handleViewReports = (userId) => {
    setSelectedUserId(userId);
    setReportDetailsOpen(true);
  };

  const handleBanUser = (userId, reportCount, nickname = null, reportRoom = null) => {
    // Create a mock message object for the ban modal
    setUserToBan({
      userId: userId,
      messageText: nickname ? `User: ${nickname}` : `User with ${reportCount} report(s)`,
      nickname: nickname || 'Reported User',
      actualRoom: reportRoom // Pass the report's room for proper report resolution
    });
    setBanModalOpen(true);
  };

  const handleBanComplete = () => {
    setBanModalOpen(false);
    setUserToBan(null);
    // Refresh reports and bans list after successful ban
    fetchReports();
    fetchRecentBans(bansPage, bansSearch);
    fetchUnderageBans(underageBansPage, underageBansSearch);
    fetchVpnBans(vpnBansPage, vpnBansSearch);
    // Notify parent to refresh count
    if (onReportsChanged) onReportsChanged();
  };

  const handleReportDetailsClose = () => {
    setReportDetailsOpen(false);
    setSelectedUserId(null);
    // Refresh reports in case any were resolved
    fetchReports();
    // Notify parent to refresh count
    if (onReportsChanged) onReportsChanged();
  };

  if (!isOpen) return null;

  const totalReports = reports.reduce((sum, report) => sum + report.reportCount, 0);
  const totalUsers = reports.length;

  return (
    <FullScreenOverlay>
      <Header>
        <Title>
          Reports Dashboard
          <RoomBadge>{room === 'global' ? 'All Default Rooms' : room.toUpperCase()}</RoomBadge>
        </Title>
        <CloseButton onClick={onClose}>×</CloseButton>
      </Header>

      <Content>
        <UserSearchSection>
          <SectionTitle style={{ marginBottom: '0.25rem' }}>Room Slow Mode</SectionTitle>
          <SlowModeHint>
            Throttle all non-mods in {room === 'global' ? 'a room' : 'this room'} to one message per
            interval. Use it when the room is flooded. Mods are exempt. Resets if the server restarts.
          </SlowModeHint>
          {room === 'global' && (
            <SlowModeRoomRow>
              <SlowModeRoomLabel>Room:</SlowModeRoomLabel>
              <SlowModeRoomSelect
                value={slowModeTargetRoom}
                onChange={(e) => setSlowModeTargetRoom(e.target.value)}
                disabled={slowModeSaving}
              >
                {slowModeRooms.length === 0 && (
                  <option value={slowModeTargetRoom}>{slowModeTargetRoom}</option>
                )}
                {slowModeRooms.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.title ? `${r.title} (${r.id})` : r.id}
                  </option>
                ))}
              </SlowModeRoomSelect>
            </SlowModeRoomRow>
          )}
          <SlowModeButtons>
            {SLOW_MODE_PRESETS.map((s) => (
              <SlowModeButton
                key={s}
                $active={slowModeSeconds === s}
                disabled={slowModeSaving}
                onClick={() => handleSetSlowMode(s)}
              >
                {s === 0 ? 'Off' : `${s}s`}
              </SlowModeButton>
            ))}
          </SlowModeButtons>
          <SlowModeCustomRow>
            <SlowModeRoomLabel>Custom:</SlowModeRoomLabel>
            <SlowModeCustomInput
              type="number"
              min={SLOW_MODE_MIN}
              max={SLOW_MODE_MAX}
              step="1"
              placeholder="sec"
              value={slowModeCustom}
              disabled={slowModeSaving}
              onChange={(e) => setSlowModeCustom(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSetCustomSlowMode();
              }}
            />
            <SlowModeButton
              type="button"
              disabled={slowModeSaving || slowModeCustom === ''}
              onClick={handleSetCustomSlowMode}
            >
              Set
            </SlowModeButton>
          </SlowModeCustomRow>
          <SlowModeHint>
            {slowModeSeconds > 0
              ? `Slow mode is ON for ${effectiveSlowModeRoom} — 1 message per ${slowModeSeconds}s.`
              : `Slow mode is off for ${effectiveSlowModeRoom}.`}
          </SlowModeHint>
        </UserSearchSection>
        <UserSearchSection>
          <SectionTitle style={{ marginBottom: '1rem' }}>Search Users</SectionTitle>
          <SearchForm onSubmit={(e) => e.preventDefault()}>
            <SearchInput
              type="text"
              placeholder="Search by nickname or user ID (min 2 chars)..."
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
            />
            {userSearch && (
              <ClearButton
                type="button"
                onClick={() => {
                  setUserSearch('');
                  setUserSearchResults([]);
                }}
              >
                Clear
              </ClearButton>
            )}
          </SearchForm>
          {userSearchLoading && <LoadingMessage>Searching...</LoadingMessage>}
          {userSearchResults.length > 0 && (
            <UserSearchResults>
              {userSearchResults.map(user => (
                <UserSearchItem key={user.id}>
                  <OnlineIndicator $isOnline={user.isOnline} />
                  <UserSearchInfo>
                    <UserSearchName>
                      {user.nickname}
                      <UserId>({user.id.substring(0, 8)}...)</UserId>
                      {user.isOnline && <RoomBadgeSmall>{user.currentRoom}</RoomBadgeSmall>}
                      {typeof user.trustLevel === 'number' && (
                        <TrustBadgeSmall $level={user.trustLevel}>
                          {TRUST_BADGE_NAMES[user.trustLevel] || 'New'}
                          {user.trustManualOverride !== null && user.trustManualOverride !== undefined && '*'}
                        </TrustBadgeSmall>
                      )}
                    </UserSearchName>
                    <UserSearchMeta>
                      {user.isOnline ? 'Online' : `Last active ${dayjs(user.lastActiveAt).fromNow()}`}
                    </UserSearchMeta>
                  </UserSearchInfo>
                  <ActionButtons>
                    <FriendButton
                      onClick={() => handleSendFriendRequest(user.id)}
                      disabled={friendRequestLoading[user.id] || friendRequestSent[user.id]}
                    >
                      {friendRequestSent[user.id] ? 'Sent!' : friendRequestLoading[user.id] ? 'Sending...' : 'Add Friend'}
                    </FriendButton>
                    <BanButton onClick={() => handleBanUser(user.id, 0, user.nickname)}>
                      Ban User
                    </BanButton>
                  </ActionButtons>
                </UserSearchItem>
              ))}
            </UserSearchResults>
          )}
          {userSearch.length >= 2 && !userSearchLoading && userSearchResults.length === 0 && (
            <EmptyState>No users found matching "{userSearch}"</EmptyState>
          )}
        </UserSearchSection>

        <ReportsList>
          <ReportsHeader>
            <SectionTitle>Reported Users</SectionTitle>
            <StatsSection>
              <StatCard>
                <StatNumber>{totalReports}</StatNumber>
                <StatLabel>Reports</StatLabel>
              </StatCard>
              <StatCard>
                <StatNumber>{totalUsers}</StatNumber>
                <StatLabel>Users</StatLabel>
              </StatCard>
              <StatCard>
                <StatNumber>{reports.filter(r => r.reportCount >= 3).length}</StatNumber>
                <StatLabel>High Priority</StatLabel>
              </StatCard>
              <RefreshButton onClick={fetchReports} disabled={loading}>
                {loading ? 'Loading...' : 'Refresh'}
              </RefreshButton>
            </StatsSection>
          </ReportsHeader>

          {loading && reports.length === 0 ? (
            <LoadingMessage>Loading reports...</LoadingMessage>
          ) : error ? (
            <ErrorMessage>{error}</ErrorMessage>
          ) : reports.length === 0 ? (
            <EmptyState>
              No pending reports found. Great job keeping the community clean! 🎉
            </EmptyState>
          ) : (
            reports.map((report) => (
              <ReportItem key={report.userId}>
                <UserInfo>
                  <UserDisplay>
                    {report.nickname || 'Anonymous'}
                    <span style={{ opacity: 0.6 }}> (Age: {report.age || 'Unknown'})</span>
                    <UserId>({report.userId.substring(0, 8)}...)</UserId>
                  </UserDisplay>
                  <ReportCount>
                    {report.reportCount} report{report.reportCount !== 1 ? 's' : ''}
                  </ReportCount>
                  {report.reporterId && (
                    <ReportCount>
                      Reported by: {report.reporterNickname || `${report.reporterId.substring(0, 8)}...`}
                    </ReportCount>
                  )}
                  {room === 'global' && report.roomBreakdown && (
                    <RoomBreakdown>
                      {Object.entries(report.roomBreakdown).map(([roomId, count]) => (
                        <RoomBadgeSmall key={roomId}>
                          {roomId}: {count}
                        </RoomBadgeSmall>
                      ))}
                    </RoomBreakdown>
                  )}
                  <LastReportTime>
                    Latest: {dayjs(report.latestReport).fromNow()}
                  </LastReportTime>
                </UserInfo>
                <ActionButtons>
                  <ViewButton onClick={() => handleViewReports(report.userId)}>
                    View Details
                  </ViewButton>
                  <BanButton onClick={() => handleBanUser(
                    report.userId,
                    report.reportCount,
                    report.nickname,
                    // For global view, use first room from breakdown; otherwise use dashboard room
                    report.roomBreakdown ? Object.keys(report.roomBreakdown)[0] : null
                  )}>
                    Ban User
                  </BanButton>
                </ActionButtons>
              </ReportItem>
            ))
          )}
        </ReportsList>

        <BansSection>
          <BansSectionTitle>
            🚫 Recent Bans
          </BansSectionTitle>
          <SearchForm onSubmit={(e) => e.preventDefault()}>
            <SearchInput
              type="text"
              placeholder="Search by ban ID, nickname, reason..."
              value={bansSearch}
              onChange={(e) => setBansSearch(e.target.value)}
            />
            {bansSearch && (
              <ClearButton
                type="button"
                onClick={() => {
                  setBansSearch('');
                  setBansPage(1);
                }}
              >
                Clear
              </ClearButton>
            )}
          </SearchForm>
          {bansLoading && recentBans.length === 0 ? (
            <BansList>
              {[...Array(3)].map((_, index) => (
                <SkeletonBanItem key={index}>
                  <SkeletonLine width="40%" height="1.2rem" marginBottom="0.75rem" />
                  <SkeletonLine width="60%" height="0.9rem" marginBottom="0.5rem" />
                  <SkeletonLine width="80%" height="0.9rem" marginBottom="0.5rem" />
                  <SkeletonLine width="70%" height="0.9rem" marginBottom="0" />
                </SkeletonBanItem>
              ))}
            </BansList>
          ) : recentBans.length > 0 ? (
            <>
              <BansList style={{ opacity: bansLoading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
              {recentBans.map((ban, index) => (
                <BanItem key={index}>
                  <BanDetails>
                    <BanInfo>
                      <BanId>
                        <BanIdLabel>{ban.id ? 'Ban ID: ' : 'User-level ban'}</BanIdLabel>
                        {ban.id || ''}
                      </BanId>
                      {(ban.bannedUser || ban.bannedUserNickname) && (
                        <BannedUser>
                          <BannedUserLabel>Banned user: </BannedUserLabel>
                          {ban.bannedUserNickname || ban.bannedUser?.nickname || ban.bannedUser?.accountNumber || 'Unknown'}
                          {ban.bannedUser?.age && ` (Age: ${ban.bannedUser.age})`}
                        </BannedUser>
                      )}
                      {ban.bannedByMod && (
                        <BannedUser>
                          <BannedUserLabel>Banned by: </BannedUserLabel>
                          {ban.bannedByMod.nickname}
                        </BannedUser>
                      )}
                      <BanExpiry style={{ marginBottom: '0.5rem', marginTop: 0 }}>
                        Expires: {dayjs(ban.unbanDate).format('MMM D, YYYY h:mm A')}
                      </BanExpiry>
                      <InternalNote>
                        <InternalNoteLabel>Internal Note: </InternalNoteLabel>
                        {ban.internalNote}
                      </InternalNote>
                      <ExternalReason>
                        <ExternalReasonLabel>Reason shown to banned user: </ExternalReasonLabel>
                        {ban.externalReason}
                      </ExternalReason>
                      {ban.bannedMessage && (
                        <BannedMessage>
                          <BannedMessageLabel>Message: </BannedMessageLabel>
                          "{ban.bannedMessage}"
                        </BannedMessage>
                      )}
                    </BanInfo>
                    <BanTiming>
                      <BanTime>{dayjs(ban.createdAt).format('MMM D, h:mm A')}</BanTime>
                      <UnbanButton
                        onClick={() => handleUnban(ban.userId)}
                        disabled={!ban.userId || unbanningId === ban.userId}
                      >
                        {unbanningId === ban.userId ? 'Unbanning...' : 'Unban'}
                      </UnbanButton>
                      {ban.id && (
                        <EditBanButton
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEditBanModal(ban);
                          }}
                          disabled={unbanningId === ban.userId}
                        >
                          Edit
                        </EditBanButton>
                      )}
                    </BanTiming>
                  </BanDetails>
                </BanItem>
              ))}
            </BansList>
            {bansPagination && bansPagination.totalPages > 1 && (
              <PaginationControls>
                <PaginationButton
                  onClick={() => setBansPage(prev => Math.max(1, prev - 1))}
                  disabled={bansPage === 1}
                >
                  Previous
                </PaginationButton>
                <PaginationInfo>
                  Page {bansPagination.page} of {bansPagination.totalPages} ({bansPagination.total} total)
                </PaginationInfo>
                <PaginationButton
                  onClick={() => setBansPage(prev => Math.min(bansPagination.totalPages, prev + 1))}
                  disabled={bansPage === bansPagination.totalPages}
                >
                  Next
                </PaginationButton>
              </PaginationControls>
            )}
          </>
          ) : (
            <EmptyBans>{bansSearch ? 'No bans found matching search' : 'No recent bans'}</EmptyBans>
          )}
        </BansSection>

        <BansSection>
          <BansSectionTitle>
            🛡️ VPN Auto-Bans
          </BansSectionTitle>
          <p style={{ color: 'var(--clr-sec)', opacity: 0.5, fontSize: '0.8rem', margin: '0 0 1rem 0' }}>
            These users were automatically banned for VPN/proxy detection. If a ban is a false positive, click Unban to reverse it.
          </p>
          <SearchForm onSubmit={(e) => e.preventDefault()}>
            <SearchInput
              type="text"
              placeholder="Search by ban ID, nickname..."
              value={vpnBansSearch}
              onChange={(e) => setVpnBansSearch(e.target.value)}
            />
            {vpnBansSearch && (
              <ClearButton
                type="button"
                onClick={() => {
                  setVpnBansSearch('');
                  setVpnBansPage(1);
                }}
              >
                Clear
              </ClearButton>
            )}
          </SearchForm>
          {vpnBansLoading && vpnBans.length === 0 ? (
            <BansList>
              {[...Array(3)].map((_, index) => (
                <SkeletonBanItem key={index}>
                  <SkeletonLine width="40%" height="1.2rem" marginBottom="0.75rem" />
                  <SkeletonLine width="60%" height="0.9rem" marginBottom="0.5rem" />
                  <SkeletonLine width="80%" height="0.9rem" marginBottom="0" />
                </SkeletonBanItem>
              ))}
            </BansList>
          ) : vpnBans.length > 0 ? (
            <>
              <BansList style={{ opacity: vpnBansLoading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
              {vpnBans.map((ban, index) => (
                <BanItem key={index}>
                  <BanDetails>
                    <BanInfo>
                      <BanId>
                        <BanIdLabel>Ban ID: </BanIdLabel>
                        {ban.id}
                      </BanId>
                      {ban.ipAddress && (
                        <BannedUser>
                          <BannedUserLabel>IP: </BannedUserLabel>
                          {ban.ipAddress}
                        </BannedUser>
                      )}
                      {(ban.bannedUser || ban.bannedUserNickname) && (
                        <BannedUser>
                          <BannedUserLabel>Banned user: </BannedUserLabel>
                          {ban.bannedUserNickname || ban.bannedUser?.nickname || 'Unknown'}
                          {ban.bannedUser?.age && ` (Age: ${ban.bannedUser.age})`}
                        </BannedUser>
                      )}
                      <BanExpiry style={{ marginBottom: '0.5rem', marginTop: 0 }}>
                        Expires: {dayjs(ban.unbanDate).format('MMM D, YYYY h:mm A')}
                      </BanExpiry>
                    </BanInfo>
                    <BanTiming>
                      <BanTime>{dayjs(ban.createdAt).format('MMM D, h:mm A')}</BanTime>
                      <UnbanButton
                        onClick={() => handleUnbanVpn(ban.id)}
                        disabled={unbanningVpnId === ban.id}
                      >
                        {unbanningVpnId === ban.id ? 'Unbanning...' : 'Unban'}
                      </UnbanButton>
                    </BanTiming>
                  </BanDetails>
                </BanItem>
              ))}
            </BansList>
            {vpnBansPagination && vpnBansPagination.totalPages > 1 && (
              <PaginationControls>
                <PaginationButton
                  onClick={() => setVpnBansPage(prev => Math.max(1, prev - 1))}
                  disabled={vpnBansPage === 1}
                >
                  Previous
                </PaginationButton>
                <PaginationInfo>
                  Page {vpnBansPagination.page} of {vpnBansPagination.totalPages} ({vpnBansPagination.total} total)
                </PaginationInfo>
                <PaginationButton
                  onClick={() => setVpnBansPage(prev => Math.min(vpnBansPagination.totalPages, prev + 1))}
                  disabled={vpnBansPage === vpnBansPagination.totalPages}
                >
                  Next
                </PaginationButton>
              </PaginationControls>
            )}
          </>
          ) : (
            <EmptyBans>{vpnBansSearch ? 'No VPN auto-bans found matching search' : 'No active VPN auto-bans'}</EmptyBans>
          )}
        </BansSection>

        <BansSection>
          <BansSectionTitle style={{ color: '#ff8c00' }}>
            Underage Restrictions
          </BansSectionTitle>
          <p style={{ color: 'var(--clr-sec)', opacity: 0.5, fontSize: '0.8rem', margin: '0 0 1rem 0' }}>
            These users are suspected of being underage. This restriction prevents them from accessing age-restricted rooms.
          </p>
          <SearchForm onSubmit={(e) => e.preventDefault()}>
            <SearchInput
              type="text"
              placeholder="Search by nickname, reason..."
              value={underageBansSearch}
              onChange={(e) => setUnderageBansSearch(e.target.value)}
            />
            {underageBansSearch && (
              <ClearButton
                type="button"
                onClick={() => {
                  setUnderageBansSearch('');
                  setUnderageBansPage(1);
                }}
              >
                Clear
              </ClearButton>
            )}
          </SearchForm>
          {underageBansLoading && underageBans.length === 0 ? (
            <BansList>
              {[...Array(3)].map((_, index) => (
                <SkeletonBanItem key={index} style={{ borderLeftColor: 'rgba(255, 140, 0, 0.3)' }}>
                  <SkeletonLine width="40%" height="1.2rem" marginBottom="0.75rem" />
                  <SkeletonLine width="60%" height="0.9rem" marginBottom="0.5rem" />
                  <SkeletonLine width="80%" height="0.9rem" marginBottom="0" />
                </SkeletonBanItem>
              ))}
            </BansList>
          ) : underageBans.length > 0 ? (
            <>
              <BansList style={{ opacity: underageBansLoading ? 0.5 : 1, transition: 'opacity 0.2s' }}>
                {underageBans.map((ban) => (
                  <BanItem key={ban.id} style={{ borderLeftColor: '#ff8c00' }}>
                    <BanDetails>
                      <BanInfo>
                        <BanId>
                          <BanIdLabel>Restriction ID: </BanIdLabel>
                          {ban.id}
                        </BanId>
                        {(ban.bannedUser || ban.bannedUserNickname) && (
                          <BannedUser>
                            <BannedUserLabel>Restricted user: </BannedUserLabel>
                            {ban.bannedUserNickname || ban.bannedUser?.nickname || ban.bannedUser?.accountNumber || 'Unknown'}
                            {ban.bannedUser?.age && ` (Age: ${ban.bannedUser.age})`}
                          </BannedUser>
                        )}
                        {ban.bannedByMod && (
                          <BannedUser>
                            <BannedUserLabel>Restricted by: </BannedUserLabel>
                            {ban.bannedByMod.nickname}
                          </BannedUser>
                        )}
                        <ExternalReason>
                          <ExternalReasonLabel>Reason: </ExternalReasonLabel>
                          {ban.externalReason}
                        </ExternalReason>
                      </BanInfo>
                      <BanTiming>
                        <BanTime>{dayjs(ban.createdAt).format('MMM D, h:mm A')}</BanTime>
                        <UnbanButton
                          onClick={() => handleRemoveUnderageBan(ban.id)}
                          disabled={removingUnderageBanId === ban.id}
                        >
                          {removingUnderageBanId === ban.id ? 'Removing...' : 'Remove Restriction'}
                        </UnbanButton>
                      </BanTiming>
                    </BanDetails>
                  </BanItem>
                ))}
              </BansList>
              {underageBansPagination && underageBansPagination.totalPages > 1 && (
                <PaginationControls>
                  <PaginationButton
                    onClick={() => setUnderageBansPage(prev => Math.max(1, prev - 1))}
                    disabled={underageBansPage === 1}
                  >
                    Previous
                  </PaginationButton>
                  <PaginationInfo>
                    Page {underageBansPagination.page} of {underageBansPagination.totalPages} ({underageBansPagination.total} total)
                  </PaginationInfo>
                  <PaginationButton
                    onClick={() => setUnderageBansPage(prev => Math.min(underageBansPagination.totalPages, prev + 1))}
                    disabled={underageBansPage === underageBansPagination.totalPages}
                  >
                    Next
                  </PaginationButton>
                </PaginationControls>
              )}
            </>
          ) : (
            <EmptyBans>{underageBansSearch ? 'No restrictions found matching search' : 'No active underage restrictions'}</EmptyBans>
          )}
        </BansSection>
      </Content>

      <ReportDetailsModal
        isOpen={reportDetailsOpen}
        onClose={handleReportDetailsClose}
        userId={selectedUserId}
        room={room}
        onReportResolved={onReportsChanged}
      />

      <ModBanModal
        isOpen={banModalOpen}
        onClose={() => setBanModalOpen(false)}
        message={userToBan}
        room={userToBan?.actualRoom || room}
        onBanComplete={handleBanComplete}
      />

      {editBanModalOpen && (
        <ModalOverlay onClick={handleCloseEditBanModal}>
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalTitle>Edit Ban</ModalTitle>
            {banToEdit && (
              <>
                <div style={{ marginBottom: '1rem', color: 'var(--clr-sec)', fontSize: '0.9rem' }}>
                  <strong>Ban ID:</strong> {banToEdit.id}<br />
                  <strong>User:</strong> {banToEdit.bannedUserNickname || 'Unknown'}
                </div>
                <ModalLabel>Expiry Date & Time</ModalLabel>
                <ModalInput
                  type="datetime-local"
                  value={editUnbanDate}
                  onChange={(e) => setEditUnbanDate(e.target.value)}
                  min={new Date().toISOString().slice(0, 16)}
                />
                <ModalLabel>Internal Note (visible to mods only)</ModalLabel>
                <ModalInput
                  type="text"
                  value={editInternalNote}
                  onChange={(e) => setEditInternalNote(e.target.value)}
                  placeholder="Internal note for mod reference..."
                />
                <ModalLabel>External Reason (shown to banned user)</ModalLabel>
                <ModalInput
                  type="text"
                  value={editExternalReason}
                  onChange={(e) => setEditExternalReason(e.target.value)}
                  placeholder="Reason shown to user..."
                />
                <ModalButtonGroup>
                  <ModalButton
                    className="secondary"
                    onClick={handleCloseEditBanModal}
                    disabled={editingBan}
                  >
                    Cancel
                  </ModalButton>
                  <ModalButton
                    className="primary"
                    onClick={handleEditBan}
                    disabled={editingBan}
                  >
                    {editingBan ? 'Saving...' : 'Save Changes'}
                  </ModalButton>
                </ModalButtonGroup>
              </>
            )}
          </ModalContent>
        </ModalOverlay>
      )}
    </FullScreenOverlay>
  );
};

export default ModReportsDashboard;